import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getPostAuthPath } from '../utils/authRouting';

const ProtectedRoute = ({ children, requiredRole, requireCompleteProfile }) => {
  const { isAuthenticated, user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen bg-dark flex items-center justify-center">
        <div className="text-white text-xl">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location.pathname }} />;
  }

  // Account approval gate — redirect to pending screen if not approved
  // Skip for admin roles, onboarding, and the pending-approval page itself
  const isAdminRole = ['admin', 'super-admin', 'owner', 'tech_support'].includes(user?.role);
  const isOnPendingPage = location.pathname === '/pending-approval';
  const isOnOnboarding = location.pathname === '/onboarding';
  if (
    !isAdminRole &&
    !isOnPendingPage &&
    !isOnOnboarding &&
    user?.accountStatus &&
    user.accountStatus !== 'approved' &&
    user?.profileComplete === true
  ) {
    return <Navigate to="/pending-approval" replace />;
  }

  if (requiredRole && user?.role !== requiredRole) {
    // Special handling for admin roles
    if (requiredRole === 'admin' && ['admin', 'super-admin', 'owner'].includes(user?.role)) {
      return children; // Allow access for any admin level
    }

    // Support staff (admins + tech support agents)
    if (requiredRole === 'support_staff' && ['admin', 'super-admin', 'owner', 'tech_support'].includes(user?.role)) {
      return children;
    }
    
    const redirectPath = user ? getPostAuthPath(user) : '/login';
    return <Navigate to={redirectPath} replace />;
  }

  // Optionally require complete profile for certain routes (e.g., dashboards)
  if (requireCompleteProfile && user?.profileComplete !== true) {
    const redirectPath = getPostAuthPath(user);
    return <Navigate to={redirectPath} replace />;
  }

  return children;
};

export default ProtectedRoute;
